// Small date helpers used by the Calendar component and confirmation copy.

export function isoDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function fromIso(iso) {
  // Force local midday so DST / timezone doesn't shift the calendar day.
  return new Date(`${iso}T12:00:00`)
}

export function sameDay(a, b) {
  return isoDate(a) === isoDate(b)
}

export function isPast(iso, today = new Date()) {
  const t = new Date(today)
  t.setHours(0, 0, 0, 0)
  return fromIso(iso) < t
}

export function addDays(iso, n) {
  const d = fromIso(iso)
  d.setDate(d.getDate() + n)
  return isoDate(d)
}

export function monthLabel(d) {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export function shortDate(iso) {
  return fromIso(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Slot definitions — spec §4. Two constants only:
//   SLOTS_CFG  → source of truth for morning/evening windows.
//   LEAD_HOURS → how many hours before a slot's start it stops accepting
//                new bookings. Nothing is hard-coded elsewhere.
export const LEAD_HOURS = 1
export const SLOTS_CFG = [
  { k: 'morning', from: 9,  to: 11, label: '9–11 AM' },
  { k: 'evening', from: 18, to: 21, label: '6–9 PM' },
]

// Derived legacy shape kept so existing callers don't have to change.
export const SLOTS = Object.fromEntries(
  SLOTS_CFG.map((s) => [s.k, {
    id: s.k,
    title: s.k === 'morning' ? 'Morning' : 'Evening',
    sub: s.label,
    bookableUntilHour: s.from - LEAD_HOURS,
    from: s.from,
    to: s.to,
  }]),
)
export const SLOT_ORDER = SLOTS_CFG.map((s) => s.k)

// Return the slots that are still bookable for the given date. For future
// dates, both are available; for today, filter out any that have passed
// their cutoff.
export function availableSlotsFor(iso, now = new Date()) {
  if (!iso) return []
  const todayIso = isoDate(now)
  if (iso > todayIso) return SLOT_ORDER.map((k) => SLOTS[k])
  if (iso < todayIso) return []
  const hour = now.getHours()
  return SLOT_ORDER
    .filter((k) => hour < SLOTS[k].bookableUntilHour)
    .map((k) => SLOTS[k])
}

// Earliest bookable pickup date (today if any slot is still open, otherwise
// tomorrow). Used to advance the calendar cursor and disable past days.
export function earliestPickupDate(now = new Date()) {
  const today = isoDate(now)
  if (availableSlotsFor(today, now).length > 0) return today
  return addDays(today, 1)
}

// The pickup dates a service may be booked for, as { min, max }.
// `max: null` means no upper bound.
//
// Same-Day is the only service with a ceiling. It is picked up in the
// morning and returned the same evening, so it only works on a day whose
// morning slot is still ahead: today while the cutoff hasn't passed,
// otherwise tomorrow — and never further out. Nothing used to stop a
// customer paging the calendar forward, and GL-1109 was booked on 8 Oct
// for 20 Nov: a Same-Day six weeks away, which means nothing.
//
// One definition, read by the chips, the calendar bounds and the final
// validation, so the three can't drift into disagreeing about what is
// bookable.
export function pickupWindowFor(serviceKey, now = new Date()) {
  const today = isoDate(now)
  const earliest = earliestPickupDate(now)
  if (serviceKey !== 'same_day') return { min: earliest, max: null }
  const morningOpen = availableSlotsFor(today, now).some((s) => s.id === 'morning')
  const tomorrow = addDays(today, 1)
  return { min: morningOpen ? today : tomorrow, max: tomorrow }
}

// Build the 6×7 day grid for the given month, only including days from that
// month (blanks elsewhere) so the Calendar component can render clean.
export function monthGrid(year, month) {
  const first = new Date(year, month, 1)
  const startDay = first.getDay() // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < startDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ iso: isoDate(new Date(year, month, d)), day: d })
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}
